import {
  countryResourceMatching,
  preferredBroadcastCode,
  preferredBroadcastCodeSystem,
  type CountryResource,
} from "./countryResource";
import {
  type LeagueAthleteFieldDefinition,
  type LeagueAthleteUniversalFieldRole,
} from "./fieldDefinition";
import { dynamicValueIsEmpty, type LeagueDynamicFieldValue } from "./dynamicFieldValue";

export interface ScopedDynamicValueMaps {
  publicValues: Record<string, LeagueDynamicFieldValue>;
  organizationValues: Record<string, LeagueDynamicFieldValue>;
  leagueValues: Record<string, LeagueDynamicFieldValue>;
}

export function valuesByDerivingAthleteUniversalFields(
  values: Record<string, LeagueDynamicFieldValue>,
  fields: LeagueAthleteFieldDefinition[],
  countries: CountryResource[] = [],
  now?: Date,
): Record<string, LeagueDynamicFieldValue> {
  if (!fields.some((field) => field.universalAthleteRole !== undefined)) return values;

  const nextValues = { ...values };
  const fieldIDByRole = fieldIDsByAthleteRole(fields);
  deriveNameFields(nextValues, fieldIDByRole);
  deriveCountryFields(nextValues, fieldIDByRole, fields, countries);

  const updatedFieldID = fieldIDByRole.get("last-updated-at");
  if (updatedFieldID && now && valueIsMissing(nextValues[updatedFieldID])) {
    nextValues[updatedFieldID] = { kind: "date", value: now };
  }

  return nextValues;
}

export function scopedMapsByDerivingAthleteUniversalFields(
  maps: ScopedDynamicValueMaps,
  fields: LeagueAthleteFieldDefinition[],
  countries: CountryResource[] = [],
  now?: Date,
): ScopedDynamicValueMaps {
  const values = valuesByDerivingAthleteUniversalFields(mergedScopedValues(maps), fields, countries, now);
  return scopedMapsForValues(values, fields, maps);
}

export function applyCountrySelection(
  country: CountryResource,
  values: Record<string, LeagueDynamicFieldValue>,
  fields: LeagueAthleteFieldDefinition[],
): Record<string, LeagueDynamicFieldValue> {
  const nextValues = { ...values };
  const fieldIDByRole = fieldIDsByAthleteRole(fields);

  const countryNameFieldID = fieldIDByRole.get("country-name");
  if (countryNameFieldID) nextValues[countryNameFieldID] = { kind: "string", value: country.officialNameEN };

  const countryCodeFieldID = fieldIDByRole.get("country-code");
  const code = preferredBroadcastCode(country);
  if (countryCodeFieldID && code && valueIsMissing(nextValues[countryCodeFieldID])) {
    nextValues[countryCodeFieldID] = { kind: "string", value: code };
  }

  const countryCodeSystemFieldID = fieldIDByRole.get("country-code-system");
  const system = preferredBroadcastCodeSystem(country);
  if (countryCodeSystemFieldID && system && valueIsMissing(nextValues[countryCodeSystemFieldID])) {
    nextValues[countryCodeSystemFieldID] = { kind: "string", value: system };
  }

  const flagURLFieldID = fieldIDByRole.get("flag-url");
  if (flagURLFieldID && country.flagURL && valueIsMissing(nextValues[flagURLFieldID])) {
    nextValues[flagURLFieldID] = { kind: "string", value: country.flagURL };
  }

  return nextValues;
}

export function scopedMapsForValues(
  values: Record<string, LeagueDynamicFieldValue>,
  fields: LeagueAthleteFieldDefinition[],
  baseMaps?: ScopedDynamicValueMaps,
): ScopedDynamicValueMaps {
  const maps: ScopedDynamicValueMaps = baseMaps
    ? {
        publicValues: { ...baseMaps.publicValues },
        organizationValues: { ...baseMaps.organizationValues },
        leagueValues: { ...baseMaps.leagueValues },
      }
    : { publicValues: {}, organizationValues: {}, leagueValues: {} };
  for (const field of fields) {
    delete maps.publicValues[field.id];
    delete maps.organizationValues[field.id];
    delete maps.leagueValues[field.id];

    const value = values[field.id];
    if (!value || dynamicValueIsEmpty(value)) continue;
    const scopeKey =
      field.privacyScope === "public" ? "publicValues" : field.privacyScope === "organization" ? "organizationValues" : "leagueValues";
    maps[scopeKey][field.id] = value;
  }
  return maps;
}

export function mergedScopedValues(maps: ScopedDynamicValueMaps): Record<string, LeagueDynamicFieldValue> {
  return { ...maps.leagueValues, ...maps.organizationValues, ...maps.publicValues };
}

/** Compact graphics name ("A. Kolk") from name parts; undefined unless both parts are present. */
export function shortNameFromParts(firstName: string | undefined, lastName: string | undefined): string | undefined {
  const initial = firstName ? Array.from(firstName.trim())[0] : undefined;
  const last = lastName?.trim();
  return initial && last ? `${initial}. ${last}` : undefined;
}

/**
 * Compact graphics name derived from a full name when no first/last parts exist:
 * the first word collapses to its initial and the remainder is kept whole, so
 * multi-word surnames survive ("Damien Le Mesnager" → "D. Le Mesnager").
 * Single-word names pass through unchanged.
 */
export function shortNameFromFullName(fullName: string | undefined): string | undefined {
  const trimmed = fullName?.trim();
  if (!trimmed) return undefined;
  const spaceIndex = trimmed.indexOf(" ");
  if (spaceIndex < 0) return trimmed;
  const initial = Array.from(trimmed)[0];
  return `${initial}. ${trimmed.slice(spaceIndex + 1).trim()}`;
}

function deriveNameFields(
  values: Record<string, LeagueDynamicFieldValue>,
  fieldIDByRole: Map<LeagueAthleteUniversalFieldRole, string>,
) {
  const firstName = stringValueForRole(values, fieldIDByRole, "first-name");
  const lastName = stringValueForRole(values, fieldIDByRole, "last-name");

  const fullNameFieldID = fieldIDByRole.get("full-name");
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  if (fullNameFieldID && fullName && valueIsMissing(values[fullNameFieldID])) {
    values[fullNameFieldID] = { kind: "string", value: fullName };
  }

  const shortNameFieldID = fieldIDByRole.get("short-name");
  const shortName =
    shortNameFromParts(firstName, lastName) ?? shortNameFromFullName(stringValueForRole(values, fieldIDByRole, "full-name"));
  if (shortNameFieldID && shortName && valueIsMissing(values[shortNameFieldID])) {
    values[shortNameFieldID] = { kind: "string", value: shortName };
  }
}

function deriveCountryFields(
  values: Record<string, LeagueDynamicFieldValue>,
  fieldIDByRole: Map<LeagueAthleteUniversalFieldRole, string>,
  fields: LeagueAthleteFieldDefinition[],
  countries: CountryResource[],
) {
  const countryName = stringValueForRole(values, fieldIDByRole, "country-name");
  const country = countryName ? countryResourceMatching(countryName, countries) : undefined;
  if (!country) return;
  Object.assign(values, applyCountrySelection(country, values, fields));
}

function stringValueForRole(
  values: Record<string, LeagueDynamicFieldValue>,
  fieldIDByRole: Map<LeagueAthleteUniversalFieldRole, string>,
  role: LeagueAthleteUniversalFieldRole,
): string | undefined {
  const fieldID = fieldIDByRole.get(role);
  if (!fieldID) return undefined;
  const value = values[fieldID];
  return value?.kind === "string" ? value.value.trim() || undefined : undefined;
}

function valueIsMissing(value: LeagueDynamicFieldValue | undefined): boolean {
  return !value || dynamicValueIsEmpty(value);
}

function fieldIDsByAthleteRole(fields: LeagueAthleteFieldDefinition[]): Map<LeagueAthleteUniversalFieldRole, string> {
  const fieldIDByRole = new Map<LeagueAthleteUniversalFieldRole, string>();
  for (const field of fields) {
    if (field.universalAthleteRole && !fieldIDByRole.has(field.universalAthleteRole)) {
      fieldIDByRole.set(field.universalAthleteRole, field.id);
    }
  }
  return fieldIDByRole;
}
